import type { UnknownRecord } from "./type";

// check for non-primitive type, no access to arbitrary properties
export function isNonPrimitive(value: unknown): value is object {
  return isRecord(value);
}

export function hasProperty<K extends string>(
  value: unknown,
  name: K,
): value is Record<K, unknown> {
  return isRecord(value) && name in value;
}

// allows access to arbitrary properties of value
export function isRecord(value: unknown): value is UnknownRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function asRecord(value: unknown): UnknownRecord | undefined {
  return isRecord(value) ? value : undefined;
}

/** Index an object with a string key. Assumes runtime keys match the declared type — only use on closed objects. */
export function valueIn<T extends object>(obj: T, key: string): T[keyof T] | undefined {
  return (obj as Record<string, T[keyof T]>)[key];
}

/** Typed `Object.keys`. Assumes runtime keys match the declared type — only use on closed objects. */
export function keysIn<T extends object>(obj: T): Array<keyof T> {
  return Object.keys(obj) as Array<keyof T>;
}

/** Typed `Object.entries`. Assumes runtime keys match the declared type — only use on closed objects. */
export function entriesIn<T extends object>(obj: T): Array<[keyof T, T[keyof T]]> {
  return Object.entries(obj) as Array<[keyof T, T[keyof T]]>;
}

function firstProperty(obj: UnknownRecord): string | undefined {
  for (const prop in obj) {
    return prop;
  }
}

export function firstValue<V>(obj: Record<string, V>): V {
  return obj[firstProperty(obj)!];
}

export function isEmpty(obj: UnknownRecord): boolean {
  return !firstProperty(obj);
}

type Key = string | number | symbol;

export function mapObject<K extends Key, V, KM extends Key, VM>(
  obj: Record<K, V>,
  mapper: (key: K, value: V) => [KM, VM],
): Record<KM, VM> {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => mapper(key as K, value as V)),
  ) as Record<KM, VM>;
}
